import React, { useState } from 'react';
import { Phone, User, Shield, Mic } from 'lucide-react';
import { ADMIN_PASSWORD } from '../utils/auth';

const LoginScreen = ({ onLogin }) => {
  const [mode, setMode] = useState('user');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleUserEnter = () => {
    setError('');
    onLogin('user');
  };

  const handleAdminEnter = (e) => {
    e.preventDefault();
    setError('');
    if (password === ADMIN_PASSWORD) {
      onLogin('admin');
    } else {
      setError('Yönetici şifresi hatalı. Lütfen tekrar deneyin.');
    }
  };

  return (
    <div className="flex min-h-screen text-slate-200">
      {/* Ambient cockpit backdrop */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `url('${process.env.PUBLIC_URL}/cockpit-bg.png')`,
            backgroundPosition: 'center right',
            opacity: 0.28,
            filter: 'blur(1.5px) saturate(0.85) contrast(1.05)',
            transform: 'scale(1.06)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 90% at 30% 40%, rgba(34,211,238,0.12), transparent 55%), linear-gradient(180deg, rgba(5,7,10,0.55) 0%, rgba(5,7,10,0.82) 58%, #05070a 100%)',
          }}
        />
      </div>

      <div className="flex w-full flex-col lg:flex-row">
        {/* Left — brand / visual */}
        <div className="relative flex flex-1 flex-col items-center justify-center px-8 py-12 lg:px-16">
          <div className="relative w-full max-w-md">
            {/* Wave / glow visual */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-48 w-48 rounded-full bg-accent-400/10 blur-3xl" />
            </div>
            <div className="relative rounded-2xl border border-white/10 bg-cockpit-900/60 p-6 shadow-glass backdrop-blur-xl ring-1 ring-accent-400/15">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-gradient shadow-glow-cyan">
                  <Mic className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Dijital Randevu Asistanı</p>
                  <p className="text-xs text-slate-400">Sesli görüşme · Randevu yönetimi</p>
                </div>
              </div>
              <div className="flex h-16 items-end justify-center gap-1">
                {[3, 6, 4, 8, 5, 9, 4, 7, 3, 6, 5, 8, 4].map((h, i) => (
                  <div
                    key={i}
                    className="w-1.5 rounded-full bg-gradient-to-t from-accent-600/40 to-accent-400/80"
                    style={{ height: `${h * 4}px` }}
                  />
                ))}
              </div>
              <p className="mt-4 text-center text-xs leading-relaxed text-slate-400">
                Mengerler servis randevularınızı akıllı sesli asistan ile yönetin.
              </p>
            </div>
          </div>
        </div>

        {/* Right — login card */}
        <div className="flex flex-1 items-center justify-center px-6 py-12 lg:px-12">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-cockpit-900/70 p-8 shadow-glass backdrop-blur-xl ring-1 ring-white/[0.06]">
            <div className="mb-8 flex items-center justify-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-gradient shadow-glow-cyan">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <div className="text-center sm:text-left">
                <h1 className="text-lg font-bold tracking-wide text-white">Mengerler</h1>
                <p className="text-sm text-slate-400">Randevu Asistanı</p>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-white">Panele Giriş</h2>
            <p className="mt-1 text-sm text-slate-400">
              Operasyon paneline erişmek için rolünüzü seçin.
            </p>

            {/* Role tabs */}
            <div className="mt-6 flex rounded-xl bg-white/[0.04] p-1 ring-1 ring-white/10">
              <button
                type="button"
                onClick={() => {
                  setMode('user');
                  setError('');
                  setPassword('');
                }}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-all ${
                  mode === 'user'
                    ? 'bg-accent-400/15 text-accent-200 ring-1 ring-accent-400/30'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <User className="h-4 w-4" />
                Kullanıcı
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('admin');
                  setError('');
                }}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-all ${
                  mode === 'admin'
                    ? 'bg-accent-400/15 text-accent-200 ring-1 ring-accent-400/30'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Shield className="h-4 w-4" />
                Yönetici Girişi
              </button>
            </div>

            {mode === 'user' ? (
              <div className="mt-6">
                <p className="text-sm text-slate-400">
                  Standart kullanıcı olarak panele giriş yapın. Şifre gerekmez.
                </p>
                <button type="button" onClick={handleUserEnter} className="btn-primary mt-6 w-full">
                  Panele Gir
                </button>
              </div>
            ) : (
              <form onSubmit={handleAdminEnter} className="mt-6">
                <label htmlFor="admin-password" className="mb-1.5 block text-sm font-medium text-slate-300">
                  Yönetici Şifresi
                </label>
                <input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Şifrenizi girin"
                  className="field w-full"
                  autoComplete="current-password"
                />
                {error && (
                  <p className="mt-2 text-sm text-rose-400" role="alert">
                    {error}
                  </p>
                )}
                <button type="submit" className="btn-primary mt-6 w-full">
                  Yönetici Olarak Gir
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
